import { ChannelMessageCollection, ChannelMessageDoc } from '@gooduncles/gu-app-schema';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { QueryOptions, WHERE } from 'src/utils/firebase/firebase-manager';
import {
  getNoTriggerChannelMessages,
  getTriggerChannelMessages,
  observeNoTriggerChannelMessages,
  observeTriggerChannelMessages,
} from 'src/utils/firebase/firebase-short-cut';

/**
 * trigger/noTrigger 두 컬렉션 어디서 왔는지 doc 자체에 태깅된 채널 메시지.
 *
 * trigger 즉시 발송 메시지도 scheduleStatus/scheduledAt 이 null 이라 필드값으로 컬렉션을
 * 역추론할 수 없다. 호출자가 컬렉션 정보를 잃지 않도록 fetch 경계에서 단 한 번 태그를 붙이고,
 * 이후로는 절대 재추론하지 않는 것이 이 모듈의 계약이다.
 */
export type ChannelMessageWithCollection = ChannelMessageDoc & {
  messageCollection: ChannelMessageCollection;
};

const annotate = (docs: ChannelMessageDoc[], collection: ChannelMessageCollection): ChannelMessageWithCollection[] =>
  docs.map((doc) => ({ ...doc, messageCollection: collection }));

/**
 * 두 컬렉션을 병렬 조회해 messageCollection 태그가 붙은 결과만 반환한다.
 * 정렬/병합은 호출자 책임 — 컬렉션 식별만 책임진다.
 */
export const fetchAnnotatedChannelMessages = async (
  wheres: WHERE[],
  options?: QueryOptions,
): Promise<ChannelMessageWithCollection[]> => {
  const [trigger, noTrigger] = await Promise.all([
    getTriggerChannelMessages(wheres, options),
    getNoTriggerChannelMessages(wheres, options),
  ]);
  return [...annotate(trigger, 'triggerChannelMessage'), ...annotate(noTrigger, 'noTriggerChannelMessage')];
};

/**
 * 두 컬렉션을 combineLatest 로 묶고 각 doc 에 messageCollection 을 태깅한 채로 emit.
 * 정렬/필터는 호출자가 pipe 에서 추가한다.
 */
export const observeAnnotatedChannelMessages = (
  wheres: WHERE[],
  options?: QueryOptions,
): Observable<ChannelMessageWithCollection[]> =>
  combineLatest([
    observeTriggerChannelMessages(wheres, options).pipe(map((docs) => annotate(docs, 'triggerChannelMessage'))),
    observeNoTriggerChannelMessages(wheres, options).pipe(map((docs) => annotate(docs, 'noTriggerChannelMessage'))),
  ]).pipe(map(([trigger, noTrigger]) => [...trigger, ...noTrigger]));
